import React, { useState, useMemo } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MethodologyCard from './components/MethodologyCard';
import FilterPanel from './components/FilterPanel';
import ComparisonPanel from './components/ComparisonPanel';
import StatsOverview from './components/StatsOverview';
import SortControls from './components/SortControls';

const MethodologyIntelligence = () => {
  const [filters, setFilters] = useState({
    search: '',
    registry: 'all',
    type: 'all',
    status: 'all',
    riskLevel: 'all',
    vintage: 'all',
    minRating: 0,
    hasScrutinyFlags: false,
    recentlyRevised: false,
    peerReviewed: false,
    highAdoption: false,
    minPrice: '',
    maxPrice: ''
  });

  const [sortBy, setSortBy] = useState('rating');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  const [selectedMethodologies, setSelectedMethodologies] = useState([]);
  const [isComparisonVisible, setIsComparisonVisible] = useState(false);

  // Mock methodology data
  const mockMethodologies = [
    {
      id: 'vm0007',
      name: 'REDD+ Methodology Framework (VM0007)',
      registry: 'Verra',
      version: '1.6',
      type: 'forestry',
      status: 'approved',
      riskLevel: 'medium',
      rating: 8.2,
      creditsIssued: 45000000,
      projectCount: 127,
      avgPrice: 12.50,
      additionalityScore: 7.8,
      permanenceScore: 6.5,
      measurabilityScore: 8.9,
      lastUpdated: '2024-03-15',
      description: `Methodology for REDD+ projects that reduce emissions from deforestation and forest degradation while enhancing forest carbon stocks through improved forest management.`,
      flags: ['Leakage Risk', 'Baseline Uncertainty'],
      revisions: [
        { version: '1.6', date: '2024-03-15', changes: 'Updated monitoring requirements' },
        { version: '1.5', date: '2023-11-20', changes: 'Enhanced additionality criteria' },
        { version: '1.4', date: '2023-06-10', changes: 'Revised permanence buffer requirements' }
      ],
      peerReviews: [
        { reviewer: 'Carbon Trust', rating: 4, date: '2024-02-10' },
        { reviewer: 'SCS Global', rating: 4, date: '2024-01-25' },
        { reviewer: 'TÜV SÜD', rating: 3, date: '2023-12-15' }
      ],
      verraLink: 'https://verra.org/methodologies/vm0007/'
    },
    {
      id: 'vm0042',
      name: 'Methodology for Improved Forest Management (VM0042)',
      registry: 'Verra',
      version: '2.0',
      type: 'forestry',
      status: 'revised',
      riskLevel: 'low',
      rating: 9.1,
      creditsIssued: 28000000,
      projectCount: 89,
      avgPrice: 15.75,
      additionalityScore: 9.2,
      permanenceScore: 8.7,
      measurabilityScore: 9.0,
      lastUpdated: '2024-09-10',
      description: `Methodology for projects that improve forest management practices to increase carbon sequestration and reduce emissions from forest degradation.`,
      flags: [],
      revisions: [
        { version: '2.0', date: '2024-09-10', changes: 'Major revision with enhanced MRV protocols' },
        { version: '1.3', date: '2023-08-15', changes: 'Updated carbon pool calculations' }
      ],
      peerReviews: [
        { reviewer: 'Rainforest Alliance', rating: 5, date: '2024-08-20' },
        { reviewer: 'FSC International', rating: 4, date: '2024-07-30' }
      ],
      verraLink: 'https://verra.org/methodologies/vm0042/'
    },
    {
      id: 'acm0002',
      name: 'Grid-connected Electricity Generation from Renewable Sources (ACM0002)',
      registry: 'Gold Standard',
      version: '19.0',
      type: 'renewable-energy',
      status: 'approved',
      riskLevel: 'low',
      rating: 8.8,
      creditsIssued: 125000000,
      projectCount: 456,
      avgPrice: 8.25,
      additionalityScore: 8.5,
      permanenceScore: 9.5,
      measurabilityScore: 9.2,
      lastUpdated: '2024-01-20',
      description: `Consolidated methodology for grid-connected renewable electricity generation projects including wind, solar, hydro, and biomass technologies.`,
      flags: ['Grid Factor Updates Required'],
      revisions: [
        { version: '19.0', date: '2024-01-20', changes: 'Updated grid emission factors' },
        { version: '18.2', date: '2023-09-05', changes: 'Enhanced monitoring protocols' }
      ],
      peerReviews: [
        { reviewer: 'DNV GL', rating: 4, date: '2024-01-10' },
        { reviewer: 'Bureau Veritas', rating: 5, date: '2023-12-20' }
      ],
      verraLink: 'https://cdm.unfccc.int/methodologies/DB/ACM0002'
    },
    {
      id: 'vm0026',
      name: 'Methodology for Sustainable Grassland Management (VM0026)',
      registry: 'Verra',
      version: '1.0',
      type: 'agriculture',
      status: 'under-review',
      riskLevel: 'high',
      rating: 6.3,
      creditsIssued: 2500000,
      projectCount: 23,
      avgPrice: 18.90,
      additionalityScore: 5.8,
      permanenceScore: 4.2,
      measurabilityScore: 7.1,
      lastUpdated: '2024-06-30',
      description: `Methodology for projects that implement sustainable grassland management practices to enhance soil carbon sequestration and reduce methane emissions.`,
      flags: ['Measurement Uncertainty', 'Additionality Concerns', 'Permanence Risk'],
      revisions: [
        { version: '1.0', date: '2024-06-30', changes: 'Initial methodology version' }
      ],
      peerReviews: [
        { reviewer: 'Soil Carbon Coalition', rating: 3, date: '2024-06-15' }
      ],
      verraLink: 'https://verra.org/methodologies/vm0026/'
    },
    {
      id: 'acr0001',
      name: 'Afforestation and Reforestation of Degraded Land (ACR0001)',
      registry: 'ACR',
      version: '3.1',
      type: 'forestry',
      status: 'approved',
      riskLevel: 'medium',
      rating: 7.9,
      creditsIssued: 18000000,
      projectCount: 67,
      avgPrice: 14.20,
      additionalityScore: 8.1,
      permanenceScore: 7.2,
      measurabilityScore: 8.3,
      lastUpdated: '2024-04-12',
      description: `Methodology for afforestation and reforestation projects on degraded lands that were not forested for at least 10 years prior to project implementation.`,
      flags: ['Leakage Monitoring Required'],
      revisions: [
        { version: '3.1', date: '2024-04-12', changes: 'Updated permanence requirements' },
        { version: '3.0', date: '2023-10-25', changes: 'Enhanced biodiversity safeguards' }
      ],
      peerReviews: [
        { reviewer: 'American Forest Foundation', rating: 4, date: '2024-03-20' },
        { reviewer: 'The Nature Conservancy', rating: 4, date: '2024-02-15' }
      ],
      verraLink: 'https://americancarbonregistry.org/carbon-accounting/standards-methodologies/acr0001'
    },
    {
      id: 'gs0001',
      name: 'Energy Efficiency Measures in Thermal Applications of Non-Renewable Biomass',
      registry: 'Gold Standard',
      version: '2.0',
      type: 'energy-efficiency',
      status: 'deprecated',
      riskLevel: 'high',
      rating: 5.4,
      creditsIssued: 8500000,
      projectCount: 34,
      avgPrice: 6.75,
      additionalityScore: 4.2,
      permanenceScore: 6.8,
      measurabilityScore: 5.9,
      lastUpdated: '2022-12-01',
      description: `Methodology for energy efficiency improvements in thermal applications using non-renewable biomass, now deprecated due to sustainability concerns.`,
      flags: ['Deprecated Status', 'Sustainability Issues', 'No New Projects'],
      revisions: [
        { version: '2.0', date: '2022-12-01', changes: 'Final version before deprecation' }
      ],
      peerReviews: [
        { reviewer: 'WWF', rating: 2, date: '2022-11-15' }
      ],
      verraLink: 'https://globalgoals.goldstandard.org/standards/'
    }
  ];

  // Filter and sort methodologies
  const filteredAndSortedMethodologies = useMemo(() => {
    let filtered = mockMethodologies?.filter(methodology => {
      // Search filter
      if (filters?.search && !methodology?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
          !methodology?.description?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
        return false;
      }

      // Registry filter
      if (filters?.registry !== 'all' && methodology?.registry?.toLowerCase() !== filters?.registry) {
        return false;
      }

      // Type filter
      if (filters?.type !== 'all' && methodology?.type !== filters?.type) {
        return false;
      }

      // Status filter
      if (filters?.status !== 'all' && methodology?.status !== filters?.status) {
        return false;
      }

      // Risk level filter
      if (filters?.riskLevel !== 'all' && methodology?.riskLevel !== filters?.riskLevel) {
        return false;
      }

      // Rating filter
      if (methodology?.rating < filters?.minRating) {
        return false;
      }

      // Special flags filters
      if (filters?.hasScrutinyFlags && (!methodology?.flags || methodology?.flags?.length === 0)) {
        return false;
      }

      if (filters?.recentlyRevised && methodology?.status !== 'revised') {
        return false;
      }

      if (filters?.peerReviewed && (!methodology?.peerReviews || methodology?.peerReviews?.length === 0)) {
        return false;
      }

      if (filters?.highAdoption && methodology?.creditsIssued < 20000000) {
        return false;
      }

      // Price range filter
      if (filters?.minPrice && methodology?.avgPrice < parseFloat(filters?.minPrice)) {
        return false;
      }

      if (filters?.maxPrice && methodology?.avgPrice > parseFloat(filters?.maxPrice)) {
        return false;
      }

      return true;
    });

    // Sort methodologies
    filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];

      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [mockMethodologies, filters, sortBy, sortOrder]);

  // Calculate stats
  const stats = useMemo(() => {
    return {
      highRisk: mockMethodologies?.filter(m => m?.riskLevel === 'high')?.length,
      recentlyRevised: mockMethodologies?.filter(m => m?.status === 'revised')?.length,
      peerReviewed: mockMethodologies?.filter(m => m?.peerReviews && m?.peerReviews?.length > 0)?.length,
      avgRating: mockMethodologies?.reduce((sum, m) => sum + m?.rating, 0) / mockMethodologies?.length
    };
  }, [mockMethodologies]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      registry: 'all',
      type: 'all',
      status: 'all',
      riskLevel: 'all',
      vintage: 'all',
      minRating: 0,
      hasScrutinyFlags: false,
      recentlyRevised: false,
      peerReviewed: false,
      highAdoption: false,
      minPrice: '',
      maxPrice: ''
    });
  };

  const handleSortChange = (newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  const handleMethodologySelect = (methodologyId) => {
    const methodology = mockMethodologies?.find(m => m?.id === methodologyId);
    if (methodology) {
      setSelectedMethodologies(prev => {
        const isSelected = prev?.some(m => m?.id === methodologyId);
        if (isSelected) {
          return prev?.filter(m => m?.id !== methodologyId);
        } else {
          return [...prev, methodology];
        }
      });
    }
  };

  const handleCompareMethodology = (methodology) => {
    if (!selectedMethodologies?.some(m => m?.id === methodology?.id)) {
      setSelectedMethodologies(prev => [...prev, methodology]);
    }
    setIsComparisonVisible(true);
  };

  const handleRemoveFromComparison = (methodologyId) => {
    setSelectedMethodologies(prev => prev?.filter(m => m?.id !== methodologyId));
  };

  const handleClearComparison = () => {
    setSelectedMethodologies([]);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <Icon name="Brain" size={24} className="text-primary" />
                <h1 className="text-2xl font-semibold text-card-foreground">
                  Methodology Intelligence
                </h1>
              </div>
              <p className="text-muted-foreground">
                Comprehensive analysis of carbon credit methodologies with risk assessment and peer-reviewed evaluation
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
              >
                Export Analysis
              </Button>
              <Button
                variant="primary"
                iconName="Plus"
                iconPosition="left"
              >
                Request Analysis
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats Overview */}
        <StatsOverview
          stats={stats}
          filteredCount={filteredAndSortedMethodologies?.length}
          totalCount={mockMethodologies?.length}
        />

        <div className="flex gap-6">
          {/* Filter Panel */}
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            isCollapsed={isFilterCollapsed}
            onToggle={() => setIsFilterCollapsed(!isFilterCollapsed)}
          />

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {/* Sort Controls */}
            <SortControls
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />

            {/* Results */}
            {filteredAndSortedMethodologies?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-card-foreground mb-2">
                  No methodologies found
                </h3>
                <p className="text-muted-foreground mb-4">
                  Try adjusting your filters to see more results
                </p>
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className={`grid gap-4 ${
                viewMode === 'grid' ?'grid-cols-1 lg:grid-cols-2' :'grid-cols-1'
              }`}>
                {filteredAndSortedMethodologies?.map((methodology) => (
                  <MethodologyCard
                    key={methodology?.id}
                    methodology={methodology}
                    onCompare={handleCompareMethodology}
                    isSelected={selectedMethodologies?.some(m => m?.id === methodology?.id)}
                    onSelect={handleMethodologySelect}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Comparison Panel */}
      <ComparisonPanel
        selectedMethodologies={selectedMethodologies}
        onRemove={handleRemoveFromComparison}
        onClear={handleClearComparison}
        isVisible={isComparisonVisible}
        onToggle={() => setIsComparisonVisible(!isComparisonVisible)}
      />
    </div>
  );
};

export default MethodologyIntelligence;